import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Band, Buton, Cip, Kutu, girdi } from "@/components/parcalar";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { kisaTarih } from "@/lib/mrb-toplu";
import { tarih, tarihSaat } from "@/lib/veri";
import {
  adetGirdisi, girilmisIdVar, ilkGorunum, satirAc, satirAcikMi, satirGorunurMu, satirKatla, satiraGit, suzgecEtiketleri, suzgecSec, yeniIdCikisi,
  type FormModeli, type GorunumDurumu, type KontrolMaddesi, type MetinTonu, type ParcaAlanlari, type ParcaModeli, type SatirModeli, type SonOge,
  type Suzgec, type SokumTeyit,
} from "@/lib/sokum-form";

/**
 * D-073 (U-072): söküm formunun (FM-0011) parça bölümü, Discrepancy tablosu ve "Before the print-out" listesi. Bekir'in onayladığı
 * ikinci taslak (prototip/sayfalar/sokum-form--parca-kontrol.html, 25.10.2026: "Tüm önerilerin ok. Kodlamaya başla"): satır başına bir
 * kart, parça başına sabit kolonlu bir satır, MRB'den yazılan her ID için "Same" / "Different", bitmiş satır tek satıra katlanır.
 * Renkler yalnız var olan token'lardan: sarı = yapılacak, yeşil = tamam, kırmızı = MRB'den farklı, taralı gri = gelmedi.
 *
 * Bileşenler yalnız props alır (veri `lib/sokum-form.ts`'in modelinden); kaydetmeyi çağıran ekrandır. Print-out bu sayfanın kendisidir:
 * baskıda her satır açık basılır, düğmeler ve süzgeç basılmaz, giriş kutularının yalnız değeri basılır. Parça kolonları sayfanın değil
 * parça listesinin kendi genişliğine göre değişir (container query): yan menü açıkken de hiçbir şey yana kaymaz.
 */

/** Kâğıtta giriş kutusunun yalnız değeri basılır (çerçeve, zemin ve yer tutucu basılmaz). */
export const BASKI_GIRDI = "print:h-auto print:border-0 print:bg-transparent print:px-0 print:placeholder:text-transparent";
/** Küçük giriş kutusu (adet, okunan ID): `girdi` ile aynı, düğmelerin küçük yüksekliğinde. */
const GIRDI_KUCUK = girdi.replace("h-kontrol ", "h-kontrol-kucuk ");
/** Basılı düğme ve seçili segment kâğıtta düz yazıdır. */
const BASKI_YAZI = "print:h-auto print:border-0 print:bg-transparent print:p-0";
/** Zemin ve hap renkleri tarayıcı izin verdikçe basılır; anlam hiçbir zaman yalnız zemine bağlı değildir. */
const RENKLI_BASKI = "print:[print-color-adjust:exact]";

/** Parça satırının ızgarası: listede 36rem'den darda tek kolon, 36-60rem arası üç satırlık blok, 60rem'den genişte altı kolon; kâğıtta hep altı kolon. */
const PARCA_IZGARA = [
  "grid items-start gap-x-3 gap-y-2 px-3.5 py-2",
  "grid-cols-[2rem_minmax(0,1fr)] [grid-template-areas:'no_eski'_'no_gel'_'no_oku'_'no_yeni'_'no_son']",
  "@min-[36rem]:grid-cols-[2rem_minmax(0,1fr)_minmax(0,1fr)] @min-[36rem]:[grid-template-areas:'no_eski_gel'_'no_oku_oku'_'no_yeni_son']",
  "@min-[60rem]:grid-cols-[30px_104px_minmax(142px,1fr)_minmax(250px,1.35fr)_minmax(142px,1fr)_minmax(170px,1fr)] @min-[60rem]:[grid-template-areas:'no_gel_eski_oku_yeni_son'] @min-[60rem]:items-center @min-[60rem]:gap-3",
  "print:grid-cols-[1.5rem_4.5rem_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.1fr)] print:[grid-template-areas:'no_gel_eski_oku_yeni_son'] print:items-center print:gap-2 print:px-2 print:py-1 print:text-xs",
].join(" ");
/** Kolon başlığı satırı: yalnız altı kolonlu düzende (ve kâğıtta). */
const BASLIK_IZGARA = [
  "hidden gap-3 border-t border-line bg-surface-2 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.02em] text-ink-3",
  "@min-[60rem]:grid @min-[60rem]:grid-cols-[30px_104px_minmax(142px,1fr)_minmax(250px,1.35fr)_minmax(142px,1fr)_minmax(170px,1fr)]",
  "print:grid print:grid-cols-[1.5rem_4.5rem_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.1fr)] print:gap-2 print:px-2 print:py-1",
].join(" ");
const ALAN = { no: "[grid-area:no]", gel: "[grid-area:gel]", eski: "[grid-area:eski]", oku: "[grid-area:oku]", yeni: "[grid-area:yeni]", son: "[grid-area:son]" } as const;
/** Dar düzende hücrenin üstündeki küçük kolon adı. */
const HUCRE_ADI = "mb-0.5 block text-2xs font-semibold uppercase tracking-[0.02em] text-ink-3 @min-[60rem]:hidden print:hidden";
const ZEMIN = {
  gelmedi: "bg-[repeating-linear-gradient(135deg,var(--surface-2)_0_6px,var(--surface)_6px_12px)] text-ink-3",
  kirmizi: "bg-bad-bg",
  yapilacak: "bg-warn-bg",
} as const;
const METIN_TONU: Record<MetinTonu, string> = { ok: "text-ok", warn: "text-warn", bad: "text-bad", "ink-3": "text-ink-3" };
const HAP_TONU = { ok: "bg-ok-bg text-ok", warn: "bg-warn-bg text-warn", bad: "bg-bad-bg text-bad" } as const;
/** Kart çerçevesi. Renk ünlemle yazılır: taban stildeki katman dışı `* { border-color }` kuralı katmanlı renk sınıfını ezer (MontajForm emsali). */
const CERCEVE = { bad: "border-bad!", warn: "border-warn!", line: "border-line" } as const;
/**
 * "Open", "Fold", "Go to row k →": yalnız görünümü değiştirir (izin istemez), kâğıtta basılmaz. Dokunma alanı en az 24 px (inceleme turu).
 * "Open" ve "Fold" ayrıca `ml-auto` alır (prototipin `.sk-git`i): baş sarınca satırın başına değil sağ ucuna iner (ekran denemesi).
 */
const GIT = "inline-flex min-h-6 items-center whitespace-nowrap rounded px-1 py-0.5 text-sm text-info hover:underline print:hidden";
/**
 * Kartın "Go to row k →" ile kaydırıldığı yer: üst çubuk (3.5rem) + yapışık özetin ölçülen yüksekliği (`--sokum-ozet`, SokumIlerleme) +
 * küçük bir boşluk. Özet dar ekranda birkaç satıra sarılır; sabit 9rem kartın başını özetin altına sokuyordu (inceleme turu).
 */
const KAYDIRMA_PAYI = "scroll-mt-[calc(3.5rem_+_var(--sokum-ozet,3.5rem)_+_0.5rem)]";
/** Aynı pay parça listesindeki giriş kutusu ve düğmelerde: Tab / Shift+Tab ile odaklanan alan özetin altında kalmaz. */
const ODAK_PAYI = "[&_input]:scroll-mt-[calc(3.5rem_+_var(--sokum-ozet,3.5rem)_+_0.5rem)] [&_button]:scroll-mt-[calc(3.5rem_+_var(--sokum-ozet,3.5rem)_+_0.5rem)]";

/** Kalıptaki {0}, {1} … yerine düğüm koyar (Türkçede kelime sırası değişebilir). Emsal: SokumTopluYukleme.tsx. */
function yerlestir(kalip: string, degerler: ReactNode[]): ReactNode {
  return kalip.split(/(\{\d+\})/).map((p, i) => {
    const m = /^\{(\d+)\}$/.exec(p);
    return <Fragment key={i}>{m ? degerler[Number(m[1])] : p}</Fragment>;
  });
}

/** "MRB" etiketi: değer MRB'den yazıldı (çevrilmez, bugünkü "MRB ✔" gibi). */
export function MrbEtiketi({ className }: { className?: string }) {
  return <span className={cn("ml-1.5 rounded-[5px] bg-info-bg px-1.5 py-px align-middle font-plex text-2xs font-semibold text-info", RENKLI_BASKI, className)}>MRB</span>;
}

function Hap({ metin, ton }: { metin: string; ton: keyof typeof HAP_TONU }) {
  return <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold", HAP_TONU[ton], RENKLI_BASKI)}>{metin}</span>;
}

/**
 * Kart başının ve katlı satırın hapları tek kutuda: baş dar ekranda (ya da uzun satır adında) sarınca haplar birlikte iner, biri üst
 * satırda öteki alt satırda kalmaz (ekran denemesi, 25.10.2026: tablette "4 to check" yukarıda, "1 different" aşağıda kalıyordu).
 */
function Haplar({ haplar }: { haplar: SatirModeli["haplar"] }) {
  if (!haplar.length) return null;
  return <span className="inline-flex flex-wrap items-center gap-x-3.5 gap-y-2">{haplar.map((h, i) => <Hap key={i} metin={h.metin} ton={h.ton} />)}</span>;
}

function SatirNo({ no }: { no: number }) {
  return <span className="inline-flex size-6.5 shrink-0 items-center justify-center rounded-full bg-surface-2 text-xs font-semibold text-ink-2">{no}</span>;
}

function Damga({ teyit }: { teyit: SokumTeyit | null }) {
  if (!teyit) return null;
  return <span className="text-xs text-ink-3" title={tarihSaat(teyit.ts)}>{teyit.kim} · {kisaTarih(teyit.ts)}</span>;
}

function SonHucresi({ son }: { son: readonly SonOge[] }) {
  return (
    <span className="flex flex-col gap-px">
      {son.map((o, i) => {
        switch (o.tur) {
          case "kimlik": return <span key={i} className={cn("break-all font-mono text-sm print:text-xs", o.kirmizi && "font-semibold text-bad")}>{o.metin}</span>;
          case "kalin": return <b key={i}>{o.metin}</b>;
          case "not": return <span key={i} className={cn("text-xs", METIN_TONU[o.ton])}>{o.metin}</span>;
          case "yapilacak": return <span key={i} className={cn("text-xs font-semibold", METIN_TONU[o.ton])}>{o.metin}</span>;
          case "etiket": return <span key={i}><span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium leading-4", o.ton === "bad" ? "bg-bad-bg text-bad" : "bg-warn-bg text-warn", RENKLI_BASKI)}>{o.metin}</span></span>;
        }
      })}
    </span>
  );
}

/**
 * Bir parça satırı (altı kolon): "#", "Not arrived", "Old ID", "Read on the piece?", "New ID (optional)", "Recorded as". Giriş kutuları
 * kendi kayıtlı değerleriyle anahtarlanır: sunucunun yazdığı değer (doldurma, Undo, "Use", K17, demo) ekrandakinin yerine geçer, yazmak
 * kesilmez (yalnız o kutu, yalnız kayıt yanıtından sonra yeniden kurulur). `surum` bu satırın reddedilen kaydından sonra satırın kutularını
 * kayıttaki değere döndürür (öteki satırlarda yazılan değer silinmez). `yaz` yalnız alanları verir; `gorulen`i ekran gönderirken son
 * kayıttan ekler.
 *
 * "Different" okunan alanını yalnız sayfada açar (inceleme turu, 25.10.2026): alana yazılan değer alandan çıkınca ya da Enter'la tek
 * istekte kaydedilir; boş alandan çıkmak hiçbir şey kaydetmez (alan açık kalır, parça okunacak olarak kalır). "No ID readable" yalnız
 * kendi düğmesiyle ya da boş alanda Enter'la kaydedilir. Basılı "Different"e yeniden basmak, Escape ve "Same" alanı kaydetmeden kapatır.
 * "Different" yalnız sunucunun kırmızı dediği parçada kırmızıdır (R2); sıra farkı ve MRB'nin bildiği başka değer nötr basılı görünür.
 * Kolonların DOM sırası dar düzenin okuma sırasıdır (Old ID, sonra Not arrived); ekran okuyucu adı kolon, satır ve parça numarasını taşır.
 * "New ID" kutusu çıkışta değer değiştiyse ya da kutuya yazıldıysa (aynı metin dahil) kaydeder (`yeniIdCikisi`, ikinci inceleme bulgusu).
 */
export function SokumParcaSatiri({ p, ad = "", cokParcali, kilit, surum, yaz }: { p: ParcaModeli; ad?: string; cokParcali: boolean; kilit: boolean; surum: number; yaz: (alanlar: ParcaAlanlari) => unknown }) {
  const b = p.birim;
  const [farkliAcik, setFarkliAcik] = useState(false);
  const okunanRef = useRef<HTMLInputElement>(null);
  const farkliRef = useRef<HTMLButtonElement>(null);
  const yeniRef = useRef<HTMLInputElement>(null);
  /** "New ID" kutusuna bu odakta yazıldı mı (`yeniIdCikisi`: aynı metni yeniden yazmak da kişinin değeridir; ikinci inceleme bulgusu). */
  const yeniYazildi = useRef(false);
  /** Kayıttan ya da kapanıştan sonra odağın gideceği yer (Enter → New ID, Escape → Different, "Use" → New ID). */
  const [odak, setOdak] = useState<"yeni" | "farkli" | null>(null);
  useEffect(() => {
    if (!odak) return;
    (odak === "yeni" ? yeniRef : farkliRef).current?.focus();
    setOdak(null);
  }, [odak]);
  /**
   * Açık okunan alandan, kaydetmeden çıkma niyeti. Alanın blur'u düğmenin tıklamasından önce gelir; bayrak düğmenin pointerdown'unda
   * (alan odaktaysa) kurulur, blur onu tüketir. Alan odak alınca sıfırlanır.
   */
  const vazgec = useRef(false);
  const vazgecKur = () => { vazgec.current = typeof document !== "undefined" && document.activeElement === okunanRef.current; };
  const kayitli = p.oku.tur === "farkli" ? p.oku : null;
  const okunanAcik = Boolean(kayitli) || (farkliAcik && p.oku.tur === "bekliyor");
  const parcaAdi = `${ad ? `${ad} ` : ""}#${p.no}`;
  const etiket = (sutun: string) => `${sutun} · ${parcaAdi}`;
  const kapat = (odakla: boolean) => { setFarkliAcik(false); if (odakla) setOdak("farkli"); };
  const farkliKaydet = async (deger: string) => { await yaz({ teyit: "farkli", eski: deger }); setFarkliAcik(false); };
  const okunanCik = async (deger: string) => {
    if (vazgec.current) { vazgec.current = false; return; }
    if (kayitli) {
      if (deger !== b.eski) await yaz({ eski: deger.trim() });
      return;
    }
    if (p.oku.tur !== "bekliyor" || !deger.trim()) return;
    await farkliKaydet(deger.trim());
  };
  const tus = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && !kayitli) { vazgec.current = true; kapat(true); return; }
    if (e.key !== "Enter") return;
    e.preventDefault();
    const deger = e.currentTarget.value;
    // Enter kaydeder ve odağı parçanın New ID kutusuna verir; ardından gelen blur ikinci kez kaydetmez.
    vazgec.current = true;
    if (kayitli) { if (deger !== b.eski) await yaz({ eski: deger.trim() }); }
    else if (p.oku.tur === "bekliyor") await farkliKaydet(deger.trim());
    setOdak("yeni");
  };
  /** Kutu yalnız kendi kayıtlı değeri (ya da bu satırın reddedilen kaydı) değişince yeniden kurulur; aynı satırdaki öteki kutuda yazmak kesilmez. */
  const anahtar = (adi: string, ...degerler: unknown[]) => [adi, p.k, ...degerler, surum].join(":");
  /** Basılı düğme: kırmızı yalnız sunucu kırmızı derse; yazısı zeminin üstünde (koyu temada da) `surface` rengidir. */
  const BASILI_FARKLI = p.kirmizi ? cn("border-bad! bg-bad text-surface", "print:text-bad") : cn("border-ink! bg-ink text-surface", "print:text-ink");
  const DUGME = "h-8! font-semibold print:h-auto!";

  const ayniDugmesi = (basili: boolean) => (
    <Buton kucuk islem="sokum" basili={basili} disabled={kilit} etiket={etiket(cevir("Same"))}
      className={basili ? cn(DUGME, "border-ok! bg-ok text-surface hover:opacity-90", BASKI_YAZI, "print:text-ok") : cn(DUGME, "border-ok! text-ok print:hidden")}
      onPointerDown={basili ? undefined : vazgecKur}
      onClick={basili ? undefined : () => { vazgec.current = false; setFarkliAcik(false); void yaz({ teyit: "ayni" }); }}>✓ {cevir("Same")}</Buton>
  );
  const farkliDugmesi = (basili: boolean) => (
    <Buton ref={farkliRef} kucuk islem="sokum" basili={basili} disabled={kilit} etiket={etiket(cevir("Different"))}
      className={basili ? cn(DUGME, BASILI_FARKLI, "hover:opacity-90", BASKI_YAZI) : cn(DUGME, "border-bad! text-bad print:hidden")}
      onPointerDown={basili && !kayitli ? vazgecKur : undefined}
      onClick={kayitli ? undefined : basili ? () => { vazgec.current = false; kapat(true); } : () => { vazgec.current = false; setFarkliAcik(true); }}>✗ {cevir("Different")}</Buton>
  );
  const geriAl = <Buton kucuk islem="sokum" disabled={kilit} etiket={etiket(cevir("Undo"))} className="print:hidden" onClick={() => void yaz({ teyit: null })}>{cevir("Undo")}</Buton>;

  let oku: ReactNode;
  if (p.oku.tur === "tire") oku = "—";
  else if (p.oku.tur === "not") oku = <span className={cn("text-xs", METIN_TONU[p.oku.ton])}>{p.oku.metin}</span>;
  else if (p.oku.tur === "ayni") oku = <span className="flex flex-wrap items-center gap-1.5">{ayniDugmesi(true)}<Damga teyit={p.oku.teyit} />{geriAl}</span>;
  else if (okunanAcik) {
    oku = (
      <span className="flex flex-wrap items-center gap-1.5">
        {/* Yeni açılan alanda "Same" görünür kalır: okuyan kişi aynı olduğunu görürse alanı kaydetmeden "Same"e basar. */}
        {!kayitli && ayniDugmesi(false)}
        {farkliDugmesi(true)}
        <input ref={okunanRef} key={anahtar("okunan", b.eski, b.farkli ? 1 : 0, b.teyit?.ts ?? "")} aria-label={etiket(cevir("ID on the piece"))}
          className={cn(girdi, "min-w-[7.5rem] flex-1 font-mono", p.kirmizi && "border-bad!", BASKI_GIRDI)}
          placeholder={cevir("ID on the piece")} defaultValue={kayitli ? kayitli.okunan : ""} disabled={kilit} autoFocus={!kayitli}
          onFocus={() => { vazgec.current = false; }} onKeyDown={(e) => void tus(e)} onBlur={(e) => void okunanCik(e.target.value)} />
        {!kayitli && (
          <Buton kucuk islem="sokum" disabled={kilit} etiket={etiket(cevir("No ID readable"))} className={cn(DUGME, "border-bad! text-bad print:hidden")}
            onPointerDown={vazgecKur} onClick={() => { vazgec.current = false; void farkliKaydet(""); }}>{cevir("No ID readable")}</Buton>
        )}
        {kayitli && <><Damga teyit={kayitli.teyit} />{geriAl}</>}
      </span>
    );
  } else oku = <span className="flex flex-wrap items-center gap-1.5">{ayniDugmesi(false)}{farkliDugmesi(false)}</span>;

  const eski: ReactNode = p.eski.tur === "tire" ? "—"
    : p.eski.tur === "girdi"
      ? <input key={anahtar("eski", b.eski, b.mrb ?? "")} aria-label={etiket(cevir("Old ID"))} className={cn(girdi, b.eski && "font-mono", BASKI_GIRDI)} placeholder={cevir("no ID on the piece")} defaultValue={p.eski.deger} disabled={kilit}
          onBlur={(e) => e.target.value !== b.eski && void yaz({ eski: e.target.value.trim() })} />
      : <><span className={cn("break-all font-mono text-sm print:text-xs", p.eski.ustuCizili && "text-ink-3 line-through")}>{p.eski.deger}</span><MrbEtiketi /></>;

  const yh = p.yeni;
  const yeni: ReactNode = yh.tur === "tire" ? "—" : (
    <span className="flex flex-wrap items-center gap-1.5">
      <input ref={yeniRef} key={anahtar("yeni", b.yeni)} aria-label={etiket(cevir("New ID (optional)"))} className={cn(girdi, "min-w-[7rem] flex-1", b.yeni && "font-mono", yh.gerekli && "border-warn!", BASKI_GIRDI)} placeholder={yh.yerTutucu}
        defaultValue={yh.deger} disabled={kilit} onFocus={() => { yeniYazildi.current = false; }} onChange={() => { yeniYazildi.current = true; }}
        onBlur={(e) => {
          const yeni = yeniIdCikisi(e.target.value, b.yeni, yeniYazildi.current);
          yeniYazildi.current = false;
          if (yeni !== null) void yaz({ yeni });
        }} />
      {yh.oneri && <Buton kucuk islem="sokum" disabled={kilit} etiket={etiket(cevir("Use {0}", yh.oneri))} className="print:hidden" onClick={async () => { await yaz({ yeni: yh.oneri ?? "" }); setOdak("yeni"); }}>{cevir("Use {0}", yh.oneri)}</Buton>}
    </span>
  );

  return (
    <div className={cn(PARCA_IZGARA, "border-t border-line text-base", p.zemin && ZEMIN[p.zemin], RENKLI_BASKI)} data-hal={p.hal}>
      <span className={cn(ALAN.no, "pt-2 text-center text-xs font-semibold text-ink-3 @min-[60rem]:pt-0 print:pt-0")}>{p.no}</span>
      <span className={cn(ALAN.eski, "min-w-0")}>
        <span className={HUCRE_ADI}>{cevir("Old ID")}</span>
        {eski}
        {p.hat && <span className="block text-xs text-ink-3">{p.hat}</span>}
      </span>
      <span className={cn(ALAN.gel, "min-w-0")}>
        {cokParcali && (
          <label className={cn("inline-flex min-h-6 items-center gap-1.5 text-xs text-ink-3", !b.gelmedi && "print:hidden")}>
            <input type="checkbox" aria-label={etiket(cevir("Not arrived"))} checked={Boolean(b.gelmedi)} disabled={kilit} onChange={(e) => void yaz({ gelmedi: e.target.checked })} /> {cevir("Not arrived")}
          </label>
        )}
      </span>
      <span className={cn(ALAN.oku, "min-w-0")}><span className={HUCRE_ADI}>{cevir("Read on the piece?")}</span>{oku}</span>
      <span className={cn(ALAN.yeni, "min-w-0")}><span className={HUCRE_ADI}>{cevir("New ID (optional)")}</span>{yeni}</span>
      <span className={cn(ALAN.son, "min-w-0")}><span className={HUCRE_ADI}>{cevir("Recorded as")}</span><SonHucresi son={p.son} /></span>
    </div>
  );
}

/** Kartın ve katlı satırın ortak işlemleri (kaydı ekran yapar). */
export interface SatirIslemleri {
  satirYaz: (sira: number, alanlar: Record<string, unknown>) => unknown;
  /** parçanın alanları; `gorulen`i ekran gönderirken son kayıttan ekler (inceleme turu) */
  parcaYaz: (sira: number, k: number, alanlar: ParcaAlanlari) => unknown;
  foto: (sira: number) => unknown;
  fotoKaldir: (sira: number, f: { ad: string; dokumanId: string }) => unknown;
}

/** "Arrived | Not in assembly": seçili segment yok = durum seçilmedi; geri "seçilmedi"ye dönülmez (K36). */
function DurumSegmenti({ s, kilit, satirYaz }: { s: SatirModeli; kilit: boolean; satirYaz: SatirIslemleri["satirYaz"] }) {
  const sec = (d: "geldi" | "yok") => {
    if (s.durum === d) return;
    if (d === "yok" && girilmisIdVar(s.satir) && !window.confirm(cevir("Set {0} to “Not in assembly”? The IDs entered on its pieces are cleared.", s.ad))) return;
    void satirYaz(s.sira, { durum: d });
  };
  const dugme = (d: "geldi" | "yok", metin: string, ikinci: boolean) => {
    const secili = s.durum === d;
    // İnceleme turu: kap `overflow-hidden` değil (odak halkası kesiliyordu); dış köşeler düğmelerde yuvarlanır.
    return (
      <Buton kucuk islem="sokum" basili={secili} disabled={kilit} onClick={() => sec(d)}
        className={cn("rounded-none border-0 px-3 text-sm", ikinci ? "rounded-r-[9px] border-l border-line-strong" : "rounded-l-[9px]", secili ? cn("bg-ink font-semibold text-surface hover:border-transparent", BASKI_YAZI, "print:text-ink") : "bg-surface text-ink-2 print:hidden")}>{metin}</Buton>
    );
  };
  return (
    <span role="group" aria-label={s.ad} className="inline-flex rounded-md border border-line-strong print:border-0">
      {dugme("geldi", cevir("Arrived"), false)}
      {dugme("yok", cevir("Not in assembly"), true)}
    </span>
  );
}

/**
 * Bir FM-0011 satırının kartı: baş (no, ad, adet, söken, haplar, segment), parça satırları, not / bulgu / fotoğraf, MRB kanıtı. `hata`:
 * bu satırın reddedilen kaydının cümlesi (inceleme turu: kaydın yapıldığı yerde görünür, sayfanın tepesinde değil).
 */
export function SokumSatirKarti({ s, id, kilit, surum, islem, katla, hata }: { s: SatirModeli; id?: string; kilit: boolean; surum: number; islem: SatirIslemleri; katla?: () => void; hata?: string | null }) {
  const x = s.satir;
  const geldi = s.durum === "geldi";
  const cokParcali = s.beklenen > 1;
  return (
    <div id={id} tabIndex={id ? -1 : undefined} className={cn("mx-4 my-3 rounded-lg border bg-surface print:mx-0 print:my-2 print:break-inside-avoid", KAYDIRMA_PAYI, CERCEVE[s.cerceve])} data-satir={s.no}>
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 px-3.5 py-2.5">
        <SatirNo no={s.no} />
        <span className="text-md font-semibold">{s.ad}</span>
        <span className="inline-flex items-center gap-1.5">
          {/* İnceleme turu: boş ya da geçersiz kutu adedi 0 yapmaz; kayıtlı değere döner. */}
          <input type="number" min={0} key={`qty:${x.qty}:${surum}`} aria-label={`${cevir("Quantity")} · ${s.ad}`} className={cn(GIRDI_KUCUK, "w-16 px-1.5", BASKI_GIRDI, "print:w-8")} defaultValue={x.qty} disabled={kilit}
            onBlur={(e) => {
              const n = adetGirdisi(e.target.value, x.qty);
              if (n === null) e.target.value = String(x.qty);
              else void islem.satirYaz(x.sira, { qty: n });
            }} />
          <span className="text-sm text-ink-3">{cevir("expected")}</span>
        </span>
        {x.kim && <span className="text-xs text-ink-3">{cevir("Removed by {0} · {1}", x.kim, tarih(x.tarih))}</span>}
        {!geldi && (x.not || x.fotolar.length > 0) && <Cip>{x.not}{x.bulgu ? " ⚑" : ""}{x.fotolar.length ? ` 📷${x.fotolar.length}` : ""}</Cip>}
        <span className="flex-1" />
        <Haplar haplar={s.haplar} />
        <DurumSegmenti s={s} kilit={kilit} satirYaz={islem.satirYaz} />
        {katla && s.tamam && <button type="button" className={cn(GIT, "ml-auto")} aria-label={`${cevir("Fold")} · ${s.no} ${s.ad}`} onClick={katla}>{cevir("Fold")}</button>}
      </div>
      {geldi && s.parcalar.length > 0 && (
        <>
          <div className={BASLIK_IZGARA}><span>#</span><span>{cevir("Not arrived")}</span><span>{cevir("Old ID")}</span><span>{cevir("Read on the piece?")}</span><span>{cevir("New ID (optional)")}</span><span>{cevir("Recorded as")}</span></div>
          {s.parcalar.map((p) => <SokumParcaSatiri key={p.k} p={p} ad={s.ad} cokParcali={cokParcali} kilit={kilit} surum={surum} yaz={(g) => islem.parcaYaz(x.sira, p.k, g)} />)}
        </>
      )}
      {hata && <div role="alert" className="border-t border-line px-3.5 py-2 print:hidden"><Band tip="duvar">{hata}</Band></div>}
      {geldi && (
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 border-t border-line px-3.5 py-2.5 text-sm">
          <input key={`not:${x.not ?? ""}:${surum}`} aria-label={`${cevir("Note (optional)")} · ${s.ad}`} className={cn(girdi, "max-w-sm", BASKI_GIRDI)} placeholder={cevir("Note (optional)")} defaultValue={x.not ?? ""} disabled={kilit}
            onBlur={(e) => e.target.value !== (x.not ?? "") && void islem.satirYaz(x.sira, { not: e.target.value })} />
          <label className="text-xs text-ink-3"><input type="checkbox" checked={x.bulgu} disabled={kilit} onChange={(e) => void islem.satirYaz(x.sira, { bulgu: e.target.checked })} /> {cevir("finding (found condition candidate)")}</label>
          {x.fotolar.map((f) => (
            <span key={f.dokumanId} className="inline-flex items-center gap-0.5">
              <a href={`/api/dokuman/${f.dokumanId}`} target="_blank" rel="noreferrer"><Cip>🖼 {f.ad}</Cip></a>
              {!kilit && <Buton kucuk islem="sokum" title={cevir("Remove")} className="print:hidden" onClick={() => void islem.fotoKaldir(x.sira, f)}>✕</Buton>}
            </span>
          ))}
          <Buton kucuk islem="sokum" disabled={kilit} className="print:hidden" onClick={() => void islem.foto(x.sira)}>{cevir("+ Picture")}</Buton>
        </div>
      )}
      {s.parcalar.filter((p) => p.kanit).map((p) => <Band key={"kanit-" + p.k} tip={p.kanit!.tip} className="mx-3.5 mb-2.5">{p.kanit!.metin}</Band>)}
    </div>
  );
}

/** Bitmiş satırın tek satırı: "<özet> · ✓ Done · Open". Bitmemişe dönmüş katlı satır sarı / kırmızı haplarını ve çerçevesini taşır. */
function KatliSatir({ s, id, ac }: { s: SatirModeli; id: string; ac: () => void }) {
  return (
    <div id={id} tabIndex={-1} className={cn("mx-4 my-1.5 flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-lg border px-3.5 py-2 text-base print:hidden", KAYDIRMA_PAYI, CERCEVE[s.cerceve])} data-satir={s.no}>
      <SatirNo no={s.no} />
      <span className="font-semibold">{s.ad}</span>
      <span className="text-xs text-ink-3">{s.ozet}</span>
      <span className="flex-1" />
      <Haplar haplar={s.haplar} />
      <button type="button" className={cn(GIT, "ml-auto")} aria-label={`${cevir("Open")} · ${s.no} ${s.ad}`} onClick={ac}>{cevir("Open")}</button>
    </div>
  );
}

/** Üstte yapışık özet: "60 of 64 pieces done", çubuk, üç sayaç ve süzgeç. Kâğıtta yalnız sayılar basılır. */
export function SokumIlerleme({ m, suzgec, suzgecSec: sec }: { m: FormModeli; suzgec: Suzgec; suzgecSec: (f: Suzgec) => void }) {
  const { toplam, tamam, yapilacak, kirmizi, gelmedi } = m.sayac;
  const yuzde = toplam ? Math.round((tamam / toplam) * 100) : 0;
  const etiket = suzgecEtiketleri(m);
  const sayac = (renk: string, metin: string) => <span className="inline-flex items-center gap-1.5 text-sm text-ink-2"><span className={cn("inline-block size-2.25 rounded-full", renk, RENKLI_BASKI)} />{metin}</span>;
  const ref = useRef<HTMLDivElement>(null);
  // İnceleme turu: özetin yüksekliği ölçülür (`--sokum-ozet`); kartların ve parça listesindeki alanların kaydırma payı buna göredir (dar
  // ekranda özet birkaç satıra sarılır; "Go to row k →" ve Shift+Tab ile gelinen alan özetin altında kalmasın). Belgeye `scroll-padding`
  // verilmez: kartın payıyla toplanır ve kart iki kat aşağı iner (çalışan uygulamada görüldü). Sayfadan çıkınca değişken kalkar.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof document === "undefined" || typeof ResizeObserver === "undefined") return;
    const kok = document.documentElement;
    const yaz = () => kok.style.setProperty("--sokum-ozet", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    yaz();
    const g = new ResizeObserver(yaz);
    g.observe(el);
    return () => { g.disconnect(); kok.style.removeProperty("--sokum-ozet"); };
  }, []);
  return (
    <div ref={ref} className="sticky top-14 z-20 flex flex-wrap items-center gap-x-4.5 gap-y-2.5 border-b border-line bg-surface px-4 py-3 max-sm:gap-y-1.5 max-sm:py-2 print:static">
      <b className="text-md">{cevir("{0} of {1} pieces done", tamam, toplam)}</b>
      <span className="h-2 min-w-0 flex-[1_1_220px] overflow-hidden rounded-full bg-surface-3 max-sm:hidden print:hidden"><span className="block h-full bg-ok" style={{ width: `${yuzde}%` }} /></span>
      {sayac("bg-warn", cevir("{0} to do", yapilacak))}
      {sayac("bg-bad", cevir("{0} different from MRB", kirmizi))}
      {sayac("bg-ink-3", cevir("{0} not arrived", gelmedi))}
      <span role="group" aria-label={cevir("Filter")} className="inline-flex flex-wrap gap-1 rounded-[10px] bg-surface-2 p-[3px] print:hidden">
        {(["hepsi", "yapilacak", "farkli"] as const).map((f) => (
          <Buton key={f} kucuk basili={suzgec === f} onClick={() => sec(f)}
            className={cn("border-0 px-2.5 text-sm print:hidden", suzgec === f ? "bg-surface font-semibold text-ink shadow-[var(--shadow)]" : "bg-transparent font-normal text-ink-2")}>{etiket[f]}</Buton>
        ))}
      </span>
    </div>
  );
}

/** Ekranın görünüm durumu (katlı / açık satırlar, süzgeç) ve "Go to row k →". İlk yüklemede bir kez kurulur; sonra yalnız kişi değiştirir. */
export function useSokumGorunumu(m: FormModeli | null) {
  const [d, setD] = useState<GorunumDurumu | null>(null);
  /** İnceleme turu: "Open", "Fold" ve "Go to row k →"dan sonra odak hedefe geçer (kart ya da katlı satır, `tabIndex=-1`); git kaydırır. */
  const [hedef, setHedef] = useState<{ id: string; kaydir: boolean } | null>(null);
  let durum = d;
  if (!durum && m) {
    // İlk yükleme: açık satırlar o anda bitmemiş olanlardır (React'in "önceki çizimden bilgi" deseni; yalnız bir kez).
    durum = ilkGorunum(m);
    setD(durum);
  }
  useEffect(() => {
    if (!hedef || typeof document === "undefined") return;
    const el = document.getElementById(hedef.id);
    if (hedef.kaydir) el?.scrollIntoView({ behavior: "smooth", block: "start" });
    el?.focus({ preventScroll: hedef.kaydir });
    setHedef(null);
  }, [hedef]);
  const satirId = (sira: number) => `satir-${sira + 1}`;
  return {
    durum,
    suzgecSec: (f: Suzgec) => m && setD((x) => (x ? suzgecSec(x, m, f) : x)),
    ac: (sira: number) => { setD((x) => (x ? satirAc(x, sira) : x)); setHedef({ id: satirId(sira), kaydir: false }); },
    katla: (sira: number) => { setD((x) => (x ? satirKatla(x, sira) : x)); setHedef({ id: satirId(sira), kaydir: false }); },
    git: (sira: number) => { setD((x) => (x ? satiraGit(x, sira) : x)); setHedef({ id: satirId(sira), kaydir: true }); },
    basligaGit: () => setHedef({ id: SOKUM_BASLIK_ID, kaydir: true }),
  };
}
export type SokumGorunumu = ReturnType<typeof useSokumGorunumu>;
/** "Go to the header →" hedefi (Header kutusunun sarmalayıcısı). */
export const SOKUM_BASLIK_ID = "sokum-baslik";

/**
 * "Parts recorded during disassembly — 21 rows, 64 pieces": özet, yardım satırı ve kartlar. Kart listesi `@container`dır (yapışık özetin
 * altında; yapışıklığa dokunmaz). Katlı satırın ve süzgecin gizlediği satırın tam kartı `hidden print:block` sarmalayıcıda durur:
 * kâğıtta her satır açık basılır (sarmalayıcıya konur, kartın kendi `flex` / `grid`'ini bozmasın diye).
 */
export function SokumParcalarKutusu({ m, gorunum, kilit, surum, mrbIcerikVar, islem, hata }: { m: FormModeli; gorunum: { durum: GorunumDurumu | null; suzgecSec: (f: Suzgec) => unknown; ac: (sira: number) => unknown; katla: (sira: number) => unknown }; kilit: boolean; surum: number | ((sira: number) => number); mrbIcerikVar: boolean; islem: SatirIslemleri; hata?: { sira: number; metin: string } | null }) {
  const d = gorunum.durum ?? ilkGorunum(m);
  const surumu = (sira: number) => (typeof surum === "number" ? surum : surum(sira));
  return (
    <Kutu baslik={m.baslik} padded={false}>
      <SokumIlerleme m={m} suzgec={d.suzgec} suzgecSec={(f) => void gorunum.suzgecSec(f)} />
      {!mrbIcerikVar && <Band tip="not" className="mx-4 mt-3">{cevir("This riser's")} <b>{cevir("MRB content excel")}</b> {cevir("is not uploaded to system; old IDs cannot be verified. When the record's content excel is uploaded from the MRB intake screen, the check works automatically.")}</Band>}
      <p className="px-4 pt-3 text-sm text-ink-2">{yerlestir(cevir("IDs marked {0} were filled from the MRB. Read each one on the piece and press {1} or {2}. New ID is optional: leave it empty if the piece keeps its ID. The MRB itself is never changed."), [<MrbEtiketi key="m" className="ml-0" />, <b key="a">{cevir("Same")}</b>, <b key="f">{cevir("Different")}</b>])}</p>
      <div className={cn("@container pb-3", ODAK_PAYI)}>
        {m.satirlar.map((s) => {
          const kart = (id?: string, katla?: () => void) => <SokumSatirKarti s={s} id={id} kilit={kilit} surum={surumu(s.sira)} islem={islem} katla={katla} hata={id && hata?.sira === s.sira ? hata.metin : null} />;
          const id = `satir-${s.no}`;
          if (!satirGorunurMu(d, s.sira)) return <div key={s.sira} className="hidden print:block">{kart()}</div>;
          if (satirAcikMi(d, s.sira)) return <Fragment key={s.sira}>{kart(id, () => void gorunum.katla(s.sira))}</Fragment>;
          return (
            <Fragment key={s.sira}>
              <KatliSatir s={s} id={id} ac={() => void gorunum.ac(s.sira)} />
              <div className="hidden print:block">{kart()}</div>
            </Fragment>
          );
        })}
      </div>
    </Kutu>
  );
}

/**
 * Discrepancy tablosu (Review and approval kutusunda): bugün kırmızı her parça bir satır (Part | MRB says | Read on the piece | Note);
 * altında bugünkü bir parçaya ait olmayan önceki kayıtlar. Kırmızı parça yoksa başlık ve tablo çizilmez. Kendi `@container`'ı: 44rem'den
 * dar kutuda alt alta dizilir.
 */
/** İnceleme turu: 44rem'den dar kutuda (dikey tablet) hücre adları hücrenin üstünde (kolon başlığı satırı yalnız genişte). */
const TABLO_ADI = "mr-1.5 font-sans text-2xs font-semibold uppercase tracking-[0.02em] text-ink-3 @min-[44rem]:hidden print:hidden";

export function SokumUyusmazlikTablosu({ u }: { u: FormModeli["uyusmazlik"] }) {
  if (!u.satirlar.length && !u.onceki) return null;
  const izgara = "grid gap-0.5 @min-[44rem]:grid-cols-[minmax(170px,1fr)_minmax(120px,auto)_minmax(120px,auto)_minmax(200px,1.4fr)] @min-[44rem]:gap-3 print:grid-cols-[minmax(0,1.2fr)_minmax(0,.8fr)_minmax(0,.8fr)_minmax(0,1.6fr)] print:gap-2";
  return (
    <div className="mt-4 space-y-3">
      {u.baslik && (
        <div className={cn("@container rounded-md border border-bad!", RENKLI_BASKI)}>
          <div className={cn("rounded-t-md bg-bad-bg px-3 py-2 text-base font-semibold text-bad", RENKLI_BASKI)}>{u.baslik}</div>
          <ul>
            <li className={cn(izgara, "hidden px-3 py-2 text-xs font-semibold uppercase tracking-[0.02em] text-ink-3 @min-[44rem]:grid print:grid")}><span>{cevir("Part")}</span><span>{cevir("MRB says")}</span><span>{cevir("Read on the piece")}</span><span>{cevir("Note")}</span></li>
            {u.satirlar.map((r) => (
              <li key={`${r.sira}-${r.k}`} className={cn(izgara, "border-t border-line px-3 py-2 text-base")}>
                <span>{r.parca}</span>
                <span className="break-all font-mono text-sm"><span className={TABLO_ADI}>{cevir("MRB says")}</span>{r.mrb ?? "—"}</span>
                <span className="break-all"><span className={TABLO_ADI}>{cevir("Read on the piece")}</span>
                  {r.okunan && <span className="font-mono text-sm font-semibold text-bad">{r.okunan}</span>}
                  {r.okunamadi && <span className={cn("text-xs font-semibold text-bad", r.okunan && "ml-1.5")}>{cevir("No ID readable")}</span>}
                </span>
                <span className="text-xs text-ink-3"><span className={TABLO_ADI}>{cevir("Note")}</span>{r.not}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {u.onceki && <Band tip="duvar">{cevir("Earlier records of this riser:")} {u.onceki} {cevir("— record is not deleted, resolution note added.")}</Band>}
    </div>
  );
}

/**
 * "Before the print-out": sunucunun kapı maddeleri (✓ ya da !), her eksiğin "Go to row k →" bağlantısı, "Generate print-out" ve altındaki
 * sayım ("Opens when the 4 items above are done."; sayı ret cümlesindeki madde sayısıdır). Kâğıtta basılmaz (FM-0011'in parçası değil).
 */
export function SokumPrintKontrol({ m, asnRozeti, git, basligaGit, print, mesgul }: { m: FormModeli; asnRozeti?: ReactNode; git: (sira: number) => unknown; basligaGit: () => unknown; print: () => unknown; mesgul?: boolean }) {
  const baglanti = (k: KontrolMaddesi) => !k.git ? null
    : <button type="button" className={cn(GIT, "basis-full pl-7.5 text-left sm:ml-auto sm:basis-auto sm:pl-0")} onClick={() => void (k.git!.tur === "baslik" ? basligaGit() : git(k.git!.sira))}>
        {k.git.tur === "baslik" ? cevir("Go to the header →") : cevir("Go to row {0} →", k.git.sira + 1)}
      </button>;
  return (
    <div className="rounded-lg border border-line p-4 print:hidden">
      <div className="mb-2 text-md font-semibold">{cevir("Before the print-out")}</div>
      <ul>
        {m.kontrol.map((k, i) => (
          <li key={i} className="flex flex-wrap items-start gap-x-2.5 gap-y-1 border-t border-line py-1.75 text-base first:border-t-0">
            <span className={cn("inline-flex size-5 flex-none items-center justify-center rounded-full text-xs font-bold text-surface", k.isaret === "ok" ? "bg-ok" : "bg-warn")}>{k.isaret === "ok" ? "✓" : "!"}</span>
            <span className="min-w-0">{k.metin}{k.rozet && asnRozeti ? <> {asnRozeti}</> : null}</span>
            {baglanti(k)}
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Buton islem="sokum" tur="birincil" disabled={!m.printAcik} mesgul={mesgul} onClick={() => void print()}>{cevir("Generate print-out")}</Buton>
        {m.printNotu && <span className="text-xs text-ink-3">{m.printNotu}</span>}
      </div>
    </div>
  );
}

export type { SatirModeli, ParcaModeli };
